import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { useEffect } from 'react';
import { ACDLError, ForbiddenError } from '../../__legacy__/models/error.model';
import { NotificationLevel, ToastNotification } from '../../__legacy__/modules/notifications/models/notification.model';
import { INotificationsService } from '../../__legacy__/modules/notifications/services/notifications.service';
import { IUserStore } from '../../__legacy__/modules/user/user.store';
import { WorkspaceRole } from '../../__legacy__/modules/workspaces/workspaces.store';
import { IOverlayLoaderStore } from '../common/OverlayLoader.store';
import { useJobPermissions } from '../jobs/jobPermissions';
import { ICurrentWorkspaceStore } from '../workspaces/currentWorkspace/CurrentWorkspace.store';
import { ICloudCostsStore } from './cloudCosts.store';
import { ICloudCostsApiService, ICostUsageDto, IHistoryDto } from './services/cloudCostsApi.service';

interface ICloudCostsService {
  store: ICloudCostsStore;
  notifyAboutApiError(result: ACDLError, errorCode: string, dontShowBadRequest?: boolean): Promise<void>;
  // overview
  getCloudCostsDetailsAsync(workspaceId: string): Promise<void | ACDLError>;
  getUserEmailsAsync(): Promise<void | ACDLError>;
  getCloudCostsUsageAsync(worksapceId: string): Promise<void | ACDLError>;
  refreshAsync(): Promise<void | ACDLError>;
  // actions
  changeOrder(orderBy: string | undefined, orderType: string | undefined): Promise<void>;
  changePagination(pageNumber: number, pageSize: number): Promise<void>;
  handleChangeSelectedUser(userId: string): Promise<void>;
}

export const useCloudCostsService = (): ICloudCostsService => {
  const store = useInjection(ICloudCostsStore.$);
  const apiService = useInjection(ICloudCostsApiService.$);
  const notificationsService = useInjection(INotificationsService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const overlayLoaderStore = useInjection(IOverlayLoaderStore.$);
  const userStore = useInjection(IUserStore.$);
  const permissions = useJobPermissions();

  useEffect((): void => {
    store.costsUsage = null;
  }, [currentWorkspaceStore.currentWorkspace.id]);

  const notifyAboutApiError = async (result: unknown, errorCode: TranslationKey, dontShowBadRequest: boolean = false): Promise<void> => {
    if (result instanceof ACDLError && !(result instanceof ForbiddenError) && (!dontShowBadRequest || !result.isBadRequest())) {
      await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, errorCode));
    }
  };

  const getCloudCostsUsageAsync = async (workspaceId: string): Promise<void> => {
    const { currentWorkspace } = currentWorkspaceStore;

    if (!currentWorkspace.id) {
      return;
    }

    const result: ICostUsageDto | ACDLError = await apiService.getCostUsageAsync(workspaceId);

    if (result instanceof ACDLError) {
      await notifyAboutApiError(result, 'cloud_costs.get_costs_usage_failed');
      return;
    }

    store.costsUsage = result;
  };

  const getCloudCostsDetailsAsync = async (workspaceId: string): Promise<void> => {
    const { pageNumber, pageSize, orderBy, orderType } = store.historyDataPaging;

    const request = {
      pageNumber,
      pageSize,
      orderBy,
      orderType,
    };

    const { currentWorkspace } = currentWorkspaceStore;
    if (!currentWorkspace.id) {
      return;
    }

    const { userId } = store;
    let result: IHistoryDto | ACDLError | undefined = undefined;

    if (permissions.canReadCostList(false).allowed && !userId) {
      result = await apiService.getHistoryDataAsync(workspaceId, request);
    } else if (permissions.canReadCostList(true).allowed) {
      const requestedUserId = currentWorkspaceStore.isInRole([WorkspaceRole.Trainer]) ? userStore.userInfo.id : userId || '';

      result = await apiService.getUserHistoryDataAsync(workspaceId, requestedUserId, request);
    }

    if (result === undefined) {
      await notifyAboutApiError(result, 'cloud_costs.get_history_failed');
      store.historyData = [];
      return;
    }

    if (result instanceof ACDLError) {
      await notifyAboutApiError(result, 'cloud_costs.get_history_failed');
      store.historyData = [];
      return;
    }

    if (result.paged_data.pagesCount < result.paged_data.pageNumber) {
      if (result.paged_data.pagesCount === 0) {
        store.historyData = [];
        store.chartData = {};
        return;
      }
      store.historyDataPaging.pageNumber = result.paged_data.pagesCount;
      await getCloudCostsDetailsAsync(workspaceId);
    } else {
      store.historyData = result.paged_data.data.map((item) => ({
        id: item.job_id,
        costCalculated: item.cost_calculated,
        dateCreated: item.date_created,
        jobType: item.job_type,
        jobName: item.job_name,
        isDeleted: item.is_deleted,
      }));
      if (result.paged_data.pageNumber === 1) {
        store.chartData = result.chart_data;
      }
      store.historyDataPaging = {
        ...store.historyDataPaging,
        orderBy,
        orderType,
        pageNumber: result.paged_data.pageNumber,
        pagesCount: result.paged_data.pagesCount,
        totalCount: result.paged_data.totalCount,
      };
    }
  };

  const getUserEmailsAsync = async (): Promise<void> => {
    const { currentWorkspace } = currentWorkspaceStore;
    if (!currentWorkspace.id) {
      return;
    }

    const result = await apiService.getUserEmailsAsync(currentWorkspace.id);

    if (result instanceof ACDLError) {
      await notifyAboutApiError(result, 'cloud_costs.get_users_list_failed');
      return;
    }

    store.usersList = result.map((user) => ({ value: user.id, label: user.email }));
  };

  const handleChangeSelectedUser = async (userId: string): Promise<void> => {
    store.userId = userId;
    const { pageSize } = store.historyDataPaging;

    await changePagination(1, pageSize);
  };

  const changePagination = async (pageNumber: number, pageSize: number): Promise<void> => {
    store.historyDataPaging.pageNumber = pageNumber;
    store.historyDataPaging.pageSize = pageSize;
    await refreshAsync();
  };

  const changeOrder = async (orderBy: string | undefined, orderType: string | undefined): Promise<void> => {
    store.historyDataPaging.orderBy = orderBy || '';
    store.historyDataPaging.orderType = orderType || '';
    await refreshAsync();
  };

  const refreshAsync = async (): Promise<void> => {
    const { currentWorkspace } = currentWorkspaceStore;
    if (!currentWorkspace.id) {
      return;
    }

    await overlayLoaderStore.withLoaderAsync('cloud-costs-list', async () => {
      await getCloudCostsDetailsAsync(currentWorkspace.id);
    });
  };

  return {
    store,
    notifyAboutApiError,
    getCloudCostsDetailsAsync,
    changeOrder,
    changePagination,
    handleChangeSelectedUser,
    getUserEmailsAsync,
    getCloudCostsUsageAsync,
    refreshAsync,
  };
};
